<script setup lang="ts">
import { useBreakpoints } from '@vueuse/core';
import { HEADER_OFFSET_CONFIG } from '~/composables/useDragToBottom/constants';
import { usePropertiesListingHeader } from '~/composables/usePropertiesListingHeader';
import { DependencyInjectionSymbols } from '~/core/helpers/symbols';
import { screenSizesInPixels } from '~~/utils';
import { PROPERTIES_LISTING_METHODS } from '~~/utils/symbols';

const route = useRoute();
const isMapView = computed<boolean>(() => {
	// This layout is used by listing map pages. On SSR we force map-view
	// semantics so the navbar never renders hidden on first paint.
	if (import.meta.server) return true;
	return route.path.includes('/imoveis');
});

// Access shared header drag offset from centralized composable
const { headerDragOffsetY, headerTransformStyle } =
	usePropertiesListingHeader();

// Ensure deterministic initial header state (SSR + client first paint)
// for map-view routes. This avoids rendering with stale persisted offset.
watch(
	isMapView,
	isNowMapView => {
		if (isNowMapView) {
			headerDragOffsetY.value = HEADER_OFFSET_CONFIG.MIN_OFFSET;
		}
	},
	{ immediate: true }
);

// Client-only flag to prevent hydration mismatch
const isClientMounted = ref(false);

const breakpoints = useBreakpoints(screenSizesInPixels);
const isMobileScreen = breakpoints.smaller('lg');

onMounted(() => {
	isClientMounted.value = true;
});

// On any non-mobile breakpoint, force the header back to its resting
// position so the desktop layout never inherits a mid-hide offset left
// over from a prior mobile resize.
watch(isMobileScreen, isNowMobile => {
	if (!isNowMobile) {
		headerDragOffsetY.value = HEADER_OFFSET_CONFIG.MIN_OFFSET;
	}
});

const { variant, transform, forceFinalVariant } = useHeaderAnimation({
	headerHeightInPx: HEADER_OFFSET_CONFIG.DESKTOP_HEIGHT,
	variants: ['primary', 'primary']
});

const portalHeaderInlineStyle = computed(() => {
	// Keep SSR and client hydration markup identical: no inline style
	// until mounted. Then apply smooth mobile transform based on drag offset.
	if (!isClientMounted.value) return undefined;
	if (!isMapView.value || !isMobileScreen.value) return undefined;
	return headerTransformStyle.value;
});

useState<PropertiesListingMapState>('properties-listing-map', () => ({
	fetchedData: null,
	filters: {
		transactionType: 'sell',
		countPluscodes: [],
		propertyTypes: [],
		locationSlug: 'sao-paulo-sp-brasil'
	},
	pluscodes: {},
	mapConfig: {
		zoom: 14,
		maxZoom: 17,
		minZoom: 8,
		bounds: [],
		center: {
			lat: -23.585,
			lng: -46.685
		}
	}
}));

// Provide empty methods to avoid injection errors
// The actual implementation will be in the page component
provide(PROPERTIES_LISTING_METHODS, {
	updateMapFilters: () => {}
});
provide(DependencyInjectionSymbols.shortsMobileNavTeleportToBody, true);
</script>

<template>
	<!--
		On mobile-map routes the document is the scroller (so iOS Safari
		retracts the URL bar on user gesture-scroll of the listing). That
		means the OUTER layout MUST NOT have `h-dvh overflow-hidden` —
		those classes kill body scroll and block URL-bar retraction.
		Desktop and non-map routes keep the original fixed-viewport
		behavior.
	-->
	<div
		:class="[
			'flex flex-col',
			isMapView ? 'lg:h-dvh lg:overflow-hidden' : 'h-dvh overflow-hidden'
		]"
	>
		<!--
			The literal heights below MUST match HEADER_OFFSET_CONFIG.MOBILE_HEIGHT
			(60) and DESKTOP_HEIGHT (80). They're inline Tailwind because
			JIT-generated classes can't read JS constants at build time. If you
			change the constants, change these too.
		-->
		<div
			:class="[
				'lg:overflow-visible! lg:h-[80px]',
				isMapView
					? 'fixed inset-x-0 top-0 z-30 bg-transparent lg:static lg:bg-white'
					: 'fixed inset-x-0 top-0 z-30 bg-white lg:static'
			]"
		>
			<PortalHeader
				:variant="variant"
				:transform="transform"
				:is-visible="true"
				:fixed="true"
				:style="portalHeaderInlineStyle"
				:class="['h-[60px] lg:h-[80px] overflow-hidden', 'sticky flex']"
				@nav-option-being-shown="forceFinalVariant = true"
				@nav-option-closed="forceFinalVariant = false"
			/>
			<div
				id="filters-bar"
				:class="isMapView ? 'h-0 overflow-visible' : 'mt-20'"
			/>
		</div>

		<div id="filters-sidebar" />

		<main
			class="flex grow flex-col"
			:class="{
				'md:py-0 lg:h-[calc(100dvh-5rem-5.1875rem)]': isMapView
			}"
		>
			<slot />
		</main>
	</div>
</template>
